import React, { useState, useCallback, useEffect } from 'react';
import { useRouter } from 'next/router';
import { client } from '../../../sanity/lib/client';
import { debounce } from 'lodash';
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from '@/components/ui/command';

const SearchBar: React.FC = () => {
    const [query, setQuery] = useState('');
    const [results, setResults] = useState<any[]>([]);
    const [isCommandOpen, setIsCommandOpen] = useState(false);
    const router = useRouter();

    const handleSearch = useCallback(
        debounce(async (searchTerm: string) => {
            if (searchTerm.length < 2) {
                setResults([]);
                return;
            }

            const wildcardQuery = `*${searchTerm}*`;

            const searchResult: any[] = await client.fetch(
                `*[_type == "faq" && (question match $wildcardQuery || answer match $wildcardQuery)]{
                    _type,
                    question,
                    "sectionSlug": subcategory->category->section->slug.current,
                    "categorySlug": subcategory->category->slug.current,
                    "subcategorySlug": subcategory->slug.current,
                    "faqSlug": slug.current, 
                }`,
                { wildcardQuery }
            );

            setResults(searchResult.slice(0, 5));
        }, 300),
        []
    );

    useEffect(() => {
        if (isCommandOpen) {
            handleSearch(query);
        }
    }, [query, isCommandOpen]);

    const handleResultClick = (result: any) => {
        let url = '/faq';

        if (result.sectionSlug) url += `/${result.sectionSlug}`;
        if (result.categorySlug) url += `/${result.categorySlug}`;
        if (result.subcategorySlug) url += `/${result.subcategorySlug}`;
        if (result.faqSlug) url += `/${result.faqSlug}`;

        router.push(url);
    };

    const handleButtonClick = () => {
        setIsCommandOpen(true);
    };

    return (
        <div className="relative flex justify-center mt-6 mb-10">
            <div className="absolute w-full max-w-lg shadow">
                {!isCommandOpen && (
                    <button
                        onClick={handleButtonClick}
                        className="
                            inline-flex items-center whitespace-nowrap text-base ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 cursor-pointer border border-input bg-background hover:bg-accent hover:text-accent-foreground h-11 px-8 w-full font-normal rounded-full justify-start px-lg
                        "
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
                             stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                             className="lucide lucide-search size-4 shrink-0 mr-2">
                            <circle cx="11" cy="11" r="8"></circle>
                            <path d="m21 21-4.3-4.3"></path>
                        </svg>
                        Cerca un argomento
                    </button>
                )}
                {isCommandOpen && (
                    <Command className="">
                        <CommandInput
                            placeholder="Cerca un argomento..."
                            value={query}
                            onValueChange={setQuery}
                            className="w-full px-6 py-3 rounded-full border-none focus:ring-0"
                        />
                        <CommandList className="transition-all">
                            {results.length === 0 ? (
                                <CommandEmpty></CommandEmpty>
                            ) : (
                                <CommandGroup>
                                    {results.map((result, index) => (
                                        <CommandItem
                                            key={index}
                                            onSelect={() => handleResultClick(result)}
                                            className="cursor-pointer hover:bg-accent hover:text-accent-foreground"
                                        >
                                            {result.question}
                                        </CommandItem>
                                    ))}
                                </CommandGroup>
                            )}
                        </CommandList>
                    </Command>
                )}
            </div>
        </div>
    );
};

export default SearchBar;
